import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import type { ActivityEntry } from 'shared/src/api/activity-feed-api-types';
import { ActivityCardShell } from './ActivityCardShell';
import { ActivityExpandToggle } from './ActivityExpandToggle';

type NoteCardProps = {
  entry: ActivityEntry;
  showCampaignName: boolean;
};

export function NoteCard({ entry, showCampaignName }: NoteCardProps) {
  const [expanded, setExpanded] = useState(false);
  const sanitizedBody = entry.contentBody ? sanitizeHtml(entry.contentBody) : null;

  return (
    <ActivityCardShell entry={entry} showCampaignName={showCampaignName}>
      {entry.contentTitle ? <div className="text-sm font-medium">{entry.contentTitle}</div> : null}

      {sanitizedBody ? (
        <div>
          <div
            className={`prose prose-sm max-w-none text-sm text-gray-700 ${expanded ? 'max-h-[400px] overflow-y-auto' : 'line-clamp-4'}`}
            dangerouslySetInnerHTML={{ __html: sanitizedBody }}
          />
          <ActivityExpandToggle
            expanded={expanded}
            onToggle={() => setExpanded(prev => !prev)}
            collapsedLabel="Show more"
            expandedLabel="Show less"
          />
        </div>
      ) : null}
    </ActivityCardShell>
  );
}

/* ---------------------------------------------------------------------------
 * Private helpers
 * --------------------------------------------------------------------------- */

const SANITIZE_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'a', 'span'],
  ALLOWED_ATTR: ['href', 'rel', 'class'],
  FORBID_ATTR: ['style', 'onerror', 'onload']
};

function sanitizeHtml(html: string): string {
  // Enforce rel="noopener noreferrer" on all anchors to prevent reverse tabnapping
  DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  const result = DOMPurify.sanitize(html, SANITIZE_CONFIG);
  DOMPurify.removeHook('afterSanitizeAttributes');
  return result;
}
